import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import React, { createContext, useEffect, useMemo, useReducer } from 'react';
import { getAvailableTimeStepsForCustomPeriods, getDefaultTimeStepPerPeriod, getDssMetrics, getPeriodByPeriodId } from './core/history';
import { toNumberPeriod } from './core/periods';
import TimeSteps, { underDayTimeSteps } from './enums/TimeSteps';
function throwBadAction(p) {
    throw new Error('Unknown action kind: ' + p.type);
}
const handleDssViewChange = ({ selectedMetrics: currentSelectedMetrics, selectedPeriod: currentSelectedPeriod, selectedPeriodId: currentSelectedPeriodId, selectedTimeStep: currentSelectedTimeStep, selectedBase, newDssView, selectedGDDTMax, timezone }) => {
    const derivedMetricsFromDSS = getDssMetrics(newDssView);
    // Force new array to trigger when change
    const selectedMetrics = [
        ...(newDssView !== 'NONE' ? derivedMetricsFromDSS.metrics : currentSelectedMetrics)
    ];
    const selectedPeriodId = newDssView === 'etp' && currentSelectedPeriodId === 'current_day'
        ? 'current_week'
        : currentSelectedPeriodId;
    const { since, before } = toNumberPeriod({ ...currentSelectedPeriod, span: TimeSteps.custom });
    const selectedPeriod = newDssView === 'etp' && before - since <= 24 * 60 * 60
        ? getPeriodByPeriodId({ periodId: 'current_week', timezone })
        : currentSelectedPeriod;
    const selectedTimeStep = newDssView === 'etp' && underDayTimeSteps.includes(currentSelectedTimeStep)
        ? TimeSteps.perDays
        : currentSelectedTimeStep;
    const thresholdsDisplayed = ['HPOT', 'U_CAPA'].includes(newDssView);
    const base = newDssView === 'COLD_ACCUMULATION' ? 7.2 : newDssView === 'HEAT_ACCUMULATION' ? 6 : selectedBase;
    const GDDTMax = selectedGDDTMax ?? 30;
    const aggregateByMetric = newDssView === 'U_CAPA' ? true : false;
    const result = {
        selectedDssView: newDssView,
        selectedMetrics,
        selectedMeasurementReports: derivedMetricsFromDSS.measurementReports,
        selectedPeriodId,
        selectedPeriod,
        selectedTimeStep,
        thresholdsDisplayed,
        base,
        GDDTMax,
        aggregateByMetric,
        ...(derivedMetricsFromDSS.waterIncome ? { waterIncome: derivedMetricsFromDSS.waterIncome } : {})
    };
    return result;
};
const handlePeriodIdChange = ({ newPeriodId, newPeriod, newDssView, timezone, newMetrics, state }) => {
    const selectedPeriod = getPeriodByPeriodId({
        periodId: newPeriodId,
        relativeTo: moment(new Date(Date.now())).tz(timezone),
        timezone,
        periodDefined: newPeriod
    });
    const defaultTimeStep = getDefaultTimeStepPerPeriod({
        period: selectedPeriod,
        timezone,
        selectedMetrics: newMetrics
    });
    const isSelectedTimeStepExistForNewMetric = getAvailableTimeStepsForCustomPeriods({
        period: selectedPeriod,
        timezone,
        metrics: newMetrics,
        perDevice: state.isAggregateBySerial
    }).includes(state.selectedTimeStep);
    return {
        selectedPeriodId: newPeriodId,
        selectedPeriod,
        // Defaulting on the default time steps if new period doesn't have previous timestep
        selectedTimeStep: isSelectedTimeStepExistForNewMetric
            ? state.selectedTimeStep
            : newDssView === 'HEAT_ACCUMULATION' || newDssView === 'etp'
                ? TimeSteps.perDays
                : defaultTimeStep
    };
};
const reducerBuilder = (timezone) => (state, action) => {
    switch (action.type) {
        case 'setSource':
            if (state.selectedSource !== action.newSource) {
                return {
                    ...state,
                    selectedSource: action.newSource
                };
            }
            else {
                return state;
            }
        case 'setDssView':
            if (state.selectedDssView !== action.newDssView) {
                return {
                    ...state,
                    ...handleDssViewChange({
                        newDssView: action.newDssView,
                        selectedMetrics: state.selectedMetrics,
                        selectedPeriodId: state.selectedPeriodId,
                        selectedPeriod: state.selectedPeriod,
                        selectedTimeStep: state.selectedTimeStep,
                        selectedBase: state.base,
                        selectedGDDTMax: state.GDDTMax,
                        timezone
                    }),
                    dataToDisplay: undefined
                };
            }
            else {
                return state;
            }
        case 'setMetrics': {
            const defaultTimeStep = getDefaultTimeStepPerPeriod({
                period: state.selectedPeriod,
                timezone,
                selectedMetrics: action.newMetrics
            });
            const isSelectedTimeStepExistForNewMetric = getAvailableTimeStepsForCustomPeriods({
                period: state.selectedPeriod,
                timezone,
                metrics: action.newMetrics,
                perDevice: state.isAggregateBySerial
            }).includes(state.selectedTimeStep);
            return {
                ...state,
                selectedMetrics: action.newMetrics,
                selectedDssView: 'NONE',
                // Otherwise it keep being displayed
                waterIncome: [],
                selectedTimeStep: isSelectedTimeStepExistForNewMetric
                    ? state.selectedTimeStep
                    : defaultTimeStep,
                aggregateByMetric: false
            };
        }
        case 'setPeriod':
            if (state.selectedPeriod.before != action.newPeriod.before ||
                state.selectedPeriod.since != action.newPeriod.since ||
                (action.newPeriodId != undefined && state.selectedPeriodId != action.newPeriodId)) {
                return {
                    ...state,
                    selectedPeriod: action.newPeriod,
                    ...(action.newPeriodId
                        ? handlePeriodIdChange({
                            newPeriodId: action.newPeriodId,
                            newPeriod: action.newPeriod,
                            newDssView: state.selectedDssView,
                            newMetrics: state.selectedMetrics,
                            state,
                            timezone
                        })
                        : {})
                };
            }
            else {
                return state;
            }
        case 'setTimeStep':
            if (state.selectedTimeStep !== action.newTimeStep) {
                return { ...state, selectedTimeStep: action.newTimeStep };
            }
            else {
                return state;
            }
        case 'persistFormToContext': {
            return {
                ...state,
                ...handleDssViewChange({
                    newDssView: action.form.dssView,
                    selectedMetrics: action.form.metrics,
                    selectedPeriodId: action.form.periodId,
                    selectedPeriod: action.selectedPeriod,
                    selectedTimeStep: getDefaultTimeStepPerPeriod({
                        period: action.selectedPeriod,
                        timezone,
                        selectedMetrics: action.form.metrics
                    }),
                    selectedBase: state.base,
                    selectedGDDTMax: state.GDDTMax,
                    timezone
                }),
                ...handlePeriodIdChange({
                    newPeriodId: action.form.periodId,
                    newPeriod: action.selectedPeriod,
                    timezone,
                    newDssView: action.form.dssView,
                    newMetrics: action.form.metrics,
                    state
                })
            };
        }
        case 'toggleMetric': {
            const isDeletion = state.selectedMetrics.includes(action.metric);
            const newSelectedMetric = isDeletion
                ? state.selectedMetrics.filter((m) => m !== action.metric)
                : [...state.selectedMetrics];
            if (!isDeletion) {
                newSelectedMetric[action.position] = action.metric;
            }
            const defaultTimeStep = getDefaultTimeStepPerPeriod({
                period: state.selectedPeriod,
                timezone,
                selectedMetrics: newSelectedMetric
            });
            const isSelectedTimeStepExistForNewMetric = getAvailableTimeStepsForCustomPeriods({
                period: state.selectedPeriod,
                timezone,
                metrics: newSelectedMetric,
                perDevice: state.isAggregateBySerial
            }).includes(state.selectedTimeStep);
            return {
                ...state,
                selectedMetrics: newSelectedMetric,
                selectedTimeStep: isSelectedTimeStepExistForNewMetric
                    ? state.selectedTimeStep
                    : defaultTimeStep,
                aggregateByMetric: false
            };
        }
        case 'setBase':
            if (state.base !== action.base) {
                return { ...state, base: action.base };
            }
            else {
                return state;
            }
        case 'setGDDTMax':
            if (state.GDDTMax !== action.GDDTMax) {
                return { ...state, GDDTMax: action.GDDTMax };
            }
            else {
                return state;
            }
        case 'setGDDParameters':
            return { ...state, GDDTMax: action.GDDParameters.GDDTMax, base: action.GDDParameters.base };
        case 'toggleAggregationByMetric':
            return {
                ...state,
                aggregateByMetric: !state.aggregateByMetric
            };
        case 'toggleAggregationBySerial':
            return {
                ...state,
                isAggregateBySerial: !state.isAggregateBySerial,
                dataToDisplay: undefined
            };
        case 'toggleDataInDataToDisplay': {
            const notUnDefinedDataToDisplay = state.dataToDisplay || {};
            return {
                ...state,
                // This is important to reset otherwise we won't find the data
                aggregateByMetric: false,
                dataToDisplay: action.idToToggle in notUnDefinedDataToDisplay
                    ? {
                        ...notUnDefinedDataToDisplay,
                        [action.idToToggle]: !notUnDefinedDataToDisplay[action.idToToggle]
                    }
                    : { ...notUnDefinedDataToDisplay, [action.idToToggle]: true }
            };
        }
        case 'toggleWaterIncomeIrrigations':
            return {
                ...state,
                waterIncome: state.waterIncome.includes('irrigation')
                    ? state.waterIncome.filter((elt) => 'irrigation' !== elt)
                    : [...state.waterIncome, 'irrigation']
            };
        case 'toggleWaterIncomeRainfalls':
            return {
                ...state,
                waterIncome: state.waterIncome.includes('rainfalls')
                    ? state.waterIncome.filter((elt) => 'rainfalls' !== elt)
                    : [...state.waterIncome, 'rainfalls']
            };
        case 'initDataToDisplay':
            return {
                ...state,
                dataToDisplay: action.ids.reduce((acc, id) => {
                    acc[id] = true;
                    return acc;
                }, {})
            };
        case 'toggleThresholdsDisplayed':
            return {
                ...state,
                thresholdsDisplayed: !state.thresholdsDisplayed
            };
        case 'setThresholdsProfile':
            return {
                ...state,
                thresholdsProfile: action.newThresholdsProfile
            };
        case 'updateThresholdProfileThreshold': {
            const newState = { ...state };
            const currentThresholdProfile = newState.thresholdsProfile;
            if (currentThresholdProfile) {
                currentThresholdProfile.thresholds[action.thresholdKey] = action.thresholdValue;
            }
            return newState;
        }
        case 'setPeriodAndTimeStep': {
            const newState = {
                ...state,
                selectedPeriod: action.newPeriod,
                selectedTimeStep: action.newTimeStep,
                selectedPeriodId: 'custom'
            };
            return newState;
        }
        default:
            // Force switch to be complete
            throwBadAction(action);
    }
};
const HistoryContext = createContext(undefined);
/**
 * Use function to be sure Date.now will be overrided by tests
 */
export const getDefaultHistoryState = (timezone) => ({
    selectedDssView: 'NONE',
    selectedMeasurementReports: [],
    selectedMetrics: [],
    selectedPeriod: getPeriodByPeriodId({
        periodId: 'current_week',
        relativeTo: moment(new Date(Date.now())).tz(timezone),
        timezone
    }),
    selectedPeriodId: 'current_week',
    selectedTimeStep: TimeSteps.perDays,
    isLoading: false,
    base: 6,
    GDDTMax: 30,
    isAggregateBySerial: false,
    aggregateByMetric: false,
    dataToDisplay: undefined,
    waterIncome: [],
    thresholdsDisplayed: false,
    thresholdsProfile: undefined,
    highlightThresholds: false,
    selectedSource: undefined
});
export const getIrriInitialHistoryState = (timezone, isIrritensio, override) => {
    const metric = isIrritensio ? 'HPOT' : 'U_CAPA';
    return {
        ...getDefaultHistoryState(timezone),
        selectedDssView: metric,
        selectedMetrics: [metric, 'RR', 'IRR'],
        selectedMeasurementReports: [],
        selectedPeriod: {
            since: moment().tz(timezone).add(-29, 'd').startOf('day').unix(),
            before: moment()
                .tz(timezone)
                .add(isIrritensio ? 8 : 0, 'd')
                .startOf('day')
                .unix()
        },
        selectedTimeStep: TimeSteps.perDays,
        selectedPeriodId: 'custom',
        waterIncome: ['irrigation', 'rainfalls'],
        thresholdsDisplayed: true,
        ...(override ?? {})
    };
};
const fourDaysInSeconds = 60 * 60 * 24 * 4;
export const getFertigationInitialHistoryState = (timezone) => {
    const unixNow = moment().tz(timezone).unix();
    const since = unixNow - fourDaysInSeconds;
    return {
        ...getDefaultHistoryState(timezone),
        selectedDssView: 'NONE',
        selectedMetrics: ['SWC', 'ECP'],
        selectedMeasurementReports: [],
        selectedPeriod: {
            since,
            before: unixNow
        },
        selectedTimeStep: TimeSteps.maximum,
        selectedPeriodId: 'custom'
    };
};
export const getDefaultFrostHistoryState = (timeStep, timezone, customPeriod) => {
    const defaultHistoryState = getDefaultHistoryState(timezone);
    return {
        ...defaultHistoryState,
        selectedDssView: 'FROST_TRACKING',
        selectedMetrics: ['T_WET', 'T_DRY'],
        selectedTimeStep: timeStep,
        selectedPeriod: !isNil(customPeriod) ? customPeriod : defaultHistoryState.selectedPeriod,
        selectedPeriodId: !isNil(customPeriod) ? 'custom' : 'current_day'
    };
};
const notAllowedTimeStepsForVirtualDevice = [
    TimeSteps.maximum,
    TimeSteps.perHalfHours,
    TimeSteps.perQuarterHours
];
/**
 * We want to keep history state during session so this context is a bit awkward
 * it take state and function to store state via props and expose a reducer to manage state via a state machine
 * but because we want to persist that state change made via reducer are reflect to state via useEffect
 * this way if we unmount HistoryContextProvider we keep state and next time we mont its state will be hydrated
 * with persisted state
 */
export const HistoryContextProvider = ({ timezone, children, historyState, storeState, hasVirtualDevice }) => {
    const reducer = useMemo(() => reducerBuilder(timezone), [timezone]);
    const defaultHistoryState = getDefaultHistoryState(timezone);
    const defaultPeriodId = historyState?.selectedPeriodId || defaultHistoryState.selectedPeriodId;
    const defaultPeriod = getPeriodByPeriodId({
        periodId: defaultPeriodId,
        relativeTo: moment(new Date(Date.now())).tz(timezone),
        timezone,
        periodDefined: historyState?.selectedPeriod
    });
    /**
     * Workaround to override the period for plots with a virtual device which should not be allowed to see this span,
     * but we still want to keep the period for other plots
     */
    const defaultTimeStep = hasVirtualDevice &&
        historyState?.selectedTimeStep &&
        notAllowedTimeStepsForVirtualDevice.includes(historyState?.selectedTimeStep)
        ? TimeSteps.perHours
        : historyState?.selectedTimeStep || defaultHistoryState.selectedTimeStep;
    const defaultReducerState = {
        ...defaultHistoryState,
        ...(historyState || {}),
        selectedPeriod: defaultPeriod,
        selectedPeriodId: defaultPeriodId,
        selectedTimeStep: defaultTimeStep
    };
    const [reducerState, dispatch] = useReducer(reducer, defaultReducerState);
    useEffect(() => {
        storeState?.(reducerState);
    }, [reducerState]);
    /**
     * We ensure that history state from local storage contains every key from defaultState
     * to prevent error in flow like
     * we setup local storage with only one field and lean on reducer init to default other field
     * in this case we have a first render of provider with incomplete state followed by init of reducer
     * which trigger useEffect when all fields are defaulted which store new defaulted state inside local storage
     * which render this provider again with defaulted state
     * it's only on this step that we want to display children because we are now sure that all fields are defined
     */
    const hasHistoryStateBeenDefaulted = historyState && isEqual(Object.keys(historyState), Object.keys(defaultHistoryState));
    const value = useMemo(() => ({
        historyState: historyState,
        dispatch
    }), [dispatch, historyState]);
    return hasHistoryStateBeenDefaulted ? (React.createElement(HistoryContext.Provider, { value: value }, children)) : null;
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useHistoryContext = () => {
    const historyContext = React.useContext(HistoryContext);
    if (!historyContext) {
        throw new Error('You may have forgot to render <HistoryContextProvider /> because HistoryContext is not defined');
    }
    return historyContext;
};
